import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useImageViewer } from '@ohif/ui-next';
import { useSystem, utils } from '@ohif/core';
import { useNavigate } from 'react-router-dom';
import { useViewportGrid, StudyBrowser, Separator, ScrollArea } from '@ohif/ui-next';
import { PanelStudyBrowserHeader } from './PanelStudyBrowserHeader';
import { StudyPreviewBrowser } from './StudyPreviewBrowser';
import { defaultActionIcons } from './constants';
import MoreDropdownMenu from '../../Components/MoreDropdownMenu';
import { CallbackCustomization } from 'platform/core/src/types';
import { useStudyCompareStore } from '../../stores/useStudyCompareStore';
import { isPatientMode } from '../../../../../platform/app/src/custom/utils/patientMode';
import {
  getImportedStudyBrowserEntries,
  isImportedStudyUID,
  subscribeImportedStudies,
} from '../../ViewerLayout/ImportStudy/importStudySession';
import ImportedStudyPanel from '../../ViewerLayout/ImportStudy/ImportedStudyPanel';
import { switchImportedStudy } from '../../ViewerLayout/ImportStudy/switchImportedStudy';
import { useImportedStudyStore } from '../../stores/useImportedStudyStore';
import StudyBrowserDownloadButton from '../../ViewerLayout/Download/StudyBrowserDownloadButton';
import { useJpegDownloadSelectionStore } from '../../stores/useJpegDownloadSelectionStore';
import { getDisplaySetImageIds } from '../../ViewerLayout/Download/getDisplaySetImageIds';

const { sortStudyInstances, formatDate, createStudyBrowserTabs } = utils;

const thumbnailNoImageModalities = ['SR', 'SEG', 'RTSTRUCT', 'RTPLAN', 'RTDOSE', 'DOC', 'PMAP'];

/**
 * Study Browser component that displays and manages studies and their display sets
 */
function PanelStudyBrowser({
  getImageSrc,
  getStudiesForPatientByMRN,
  requestDisplaySetCreationForStudy,
  dataSource,
  customMapDisplaySets,
  onClickUntrack,
  onDoubleClickThumbnailHandlerCallBack,
  studySeriesToolbarOnly = false,
}) {
  const { servicesManager, commandsManager, extensionManager } = useSystem();
  const { displaySetService, customizationService } = servicesManager.services;
  const navigate = useNavigate();
  const studyMode = customizationService.getCustomization('studyBrowser.studyMode') || 'all';

  const internalImageViewer = useImageViewer();
  const StudyInstanceUIDs = internalImageViewer.StudyInstanceUIDs;
  const fetchedStudiesRef = useRef(new Set());

  const [{ activeViewportId, viewports, isHangingProtocolLayout }] = useViewportGrid();
  const [activeTabName, setActiveTabName] = useState(studyMode);
  const [expandedStudyInstanceUIDs, setExpandedStudyInstanceUIDs] = useState([
    ...StudyInstanceUIDs,
  ]);
  const [hasLoadedViewports, setHasLoadedViewports] = useState(false);
  const [studyDisplayList, setStudyDisplayList] = useState([]);
  const [displaySets, setDisplaySets] = useState([]);
  const [displaySetsLoadingState, setDisplaySetsLoadingState] = useState({});
  const [thumbnailImageSrcMap, setThumbnailImageSrcMap] = useState({});
  const [jumpToDisplaySet, setJumpToDisplaySet] = useState(null);
  const [selectedStudyInstanceUIDs, setSelectedStudyInstanceUIDs] = useState<string[]>([]);
  const patientMode = isPatientMode();
  const [showStudyPreviews, setShowStudyPreviews] = useState(
    studySeriesToolbarOnly && !patientMode
  );
  const compareStudyInstanceUID = useStudyCompareStore(state => state.priorStudyInstanceUID);
  const importedStudies = useImportedStudyStore(state => state.studies);
  const primaryStudyInstanceUID = StudyInstanceUIDs[0];
  const isSelectionMode = useJpegDownloadSelectionStore(state => state.isSelectionMode);
  const selectedImageIds = useJpegDownloadSelectionStore(state => state.selectedImageIds);
  const toggleImageIds = useJpegDownloadSelectionStore(state => state.toggleImageIds);

  const [viewPresets, setViewPresets] = useState(
    customizationService.getCustomization('studyBrowser.viewPresets')
  );

  const [actionIcons, setActionIcons] = useState(defaultActionIcons);

  // multiple can be true or false
  const updateActionIconValue = actionIcon => {
    actionIcon.value = !actionIcon.value;
    const newActionIcons = [...actionIcons];
    setActionIcons(newActionIcons);
  };

  // only one is true at a time
  const updateViewPresetValue = viewPreset => {
    if (!viewPreset) {
      return;
    }
    const newViewPresets = viewPresets.map(preset => {
      preset.selected = preset.id === viewPreset.id;
      return preset;
    });
    setViewPresets(newViewPresets);
  };

  const mapDisplaySetsWithState = customMapDisplaySets || _mapDisplaySets;

  const onDoubleClickThumbnailHandler = useCallback(
    async displaySetInstanceUID => {
      const customHandler = customizationService.getCustomization(
        'studyBrowser.thumbnailDoubleClickCallback'
      ) as CallbackCustomization;

      const setupArgs = {
        activeViewportId,
        commandsManager,
        servicesManager,
        isHangingProtocolLayout,
        appConfig: extensionManager._appConfig,
      };

      const handlers = customHandler?.callbacks.map(callback => callback(setupArgs));

      for (const handler of handlers) {
        await handler(displaySetInstanceUID);
      }
      onDoubleClickThumbnailHandlerCallBack?.(displaySetInstanceUID);
    },
    [
      activeViewportId,
      commandsManager,
      servicesManager,
      isHangingProtocolLayout,
      customizationService,
    ]
  );

  // ~~ studyDisplayList
  useEffect(() => {
    setStudyDisplayList(prev => _sortStudiesByDateDesc(mergeImportedStudiesIntoList(prev)));

    return subscribeImportedStudies(studyInstanceUIDs => {
      setStudyDisplayList(prev => _sortStudiesByDateDesc(mergeImportedStudiesIntoList(prev)));
      const activeStudyInstanceUID =
        useImportedStudyStore.getState().activeStudyInstanceUID ?? studyInstanceUIDs[0];
      setExpandedStudyInstanceUIDs(prev => [...new Set([...prev, activeStudyInstanceUID])]);
      setActiveTabName('all');
    });
  }, []);

  useEffect(() => {
    if (!importedStudies.length) {
      return;
    }

    setStudyDisplayList(prev => _sortStudiesByDateDesc(mergeImportedStudiesIntoList(prev)));
  }, [importedStudies]);

  const handleImportedStudySelect = useCallback(
    async studyInstanceUID => {
      setExpandedStudyInstanceUIDs(prev => [...new Set([...prev, studyInstanceUID])]);
      setActiveTabName('all');

      await switchImportedStudy(studyInstanceUID, {
        servicesManager,
        commandsManager,
        displaySetService,
        requestDisplaySetCreationForStudy,
      });
    },
    [servicesManager, commandsManager, displaySetService, requestDisplaySetCreationForStudy]
  );

  useEffect(() => {
    // Fetch all studies for the patient in each primary study
    async function fetchStudiesForPatient(StudyInstanceUID) {
      // Skip fetching if we've already fetched this study
      if (fetchedStudiesRef.current.has(StudyInstanceUID)) {
        return;
      }

      if (isImportedStudyUID(StudyInstanceUID)) {
        fetchedStudiesRef.current.add(StudyInstanceUID);
        setStudyDisplayList(prev => _sortStudiesByDateDesc(mergeImportedStudiesIntoList(prev)));
        return;
      }

      fetchedStudiesRef.current.add(StudyInstanceUID);

      // current study qido
      let qidoForStudyUID;
      try {
        // eslint-disable-next-line react/prop-types
        qidoForStudyUID = await dataSource.query.studies.search({
          studyInstanceUid: StudyInstanceUID,
        });

        if (!qidoForStudyUID?.length) {
          // Empty QIDO can mean bad UID / auth / transient failure — not necessarily expiry.
          // Only treat HTTP 410 as expired (handled in catch below).
          navigate('/notfoundstudy', { replace: true });
          if (!patientMode) {
            throw new Error('Invalid study URL');
          }
          return;
        }
      } catch (error) {
        const statusCode =
          (error as { statusCode?: number; status?: number })?.statusCode ??
          (error as { statusCode?: number; status?: number })?.status;
        const isExpired = statusCode === 410;

        if (isExpired) {
          try {
            sessionStorage.setItem('ris_patient_link_study_uids', StudyInstanceUID);
          } catch {
            // ignore storage errors
          }
          navigate('/notfoundstudy?reason=expired', { replace: true });
          return;
        }

        // If study search fails with "Report not found", don't proceed
        if (error instanceof Error && error.message.includes('Report not found')) {
          console.warn(`Study not found for ${StudyInstanceUID}, skipping patient studies fetch`);
          return;
        }
        // Re-throw other errors
        throw error;
      }

      let qidoStudiesForPatient = qidoForStudyUID;

      // try to fetch the prior studies based on the patientID if the
      // server can respond.
      try {
        qidoStudiesForPatient = await getStudiesForPatientByMRN(qidoForStudyUID);
      } catch (error) {
        console.warn(error);
      }

      const mappedStudies = _mapDataSourceStudies(qidoStudiesForPatient);
      const actuallyMappedStudies = mappedStudies.map(qidoStudy => {
        return {
          studyInstanceUid: qidoStudy.StudyInstanceUID,
          date: formatDate(qidoStudy.StudyDate) || '',
          rawStudyDate: qidoStudy.StudyDate || '',
          studyTime: qidoStudy.StudyTime || '',
          description: qidoStudy.StudyDescription,
          modalities: qidoStudy.ModalitiesInStudy,
          numInstances: Number(qidoStudy.NumInstances),
        };
      });

      setStudyDisplayList(prevArray => {
        const ret = [...prevArray];
        for (const study of actuallyMappedStudies) {
          if (!prevArray.find(it => it.studyInstanceUid === study.studyInstanceUid)) {
            ret.push(study);
          }
        }
        return _sortStudiesByDateDesc(ret);
      });
    }

    StudyInstanceUIDs.forEach(sid => fetchStudiesForPatient(sid));
  }, [StudyInstanceUIDs, dataSource, getStudiesForPatientByMRN, navigate]);

  // Preload series metadata for all patient studies so preview thumbnails are available.
  useEffect(() => {
    if (!showStudyPreviews || !studyDisplayList.length) {
      return;
    }

    studyDisplayList.forEach(study => {
      requestDisplaySetCreationForStudy(displaySetService, study.studyInstanceUid, true);
    });
  }, [showStudyPreviews, studyDisplayList, displaySetService, requestDisplaySetCreationForStudy]);

  // ~~ Initial Thumbnails
  useEffect(() => {
    if (!hasLoadedViewports) {
      if (activeViewportId) {
        // Once there is an active viewport id, it means the layout is ready
        // so wait a bit of time to allow the viewports preferential loading
        // which improves user experience of responsiveness significantly on slower
        // systems.
        const delayMs = 250 + displaySetService.getActiveDisplaySets().length * 10;
        window.setTimeout(() => setHasLoadedViewports(true), delayMs);
      }

      return;
    }

    let currentDisplaySets = displaySetService.activeDisplaySets;
    // filter non based on the list of modalities that are supported by cornerstone
    currentDisplaySets = currentDisplaySets.filter(
      ds => !thumbnailNoImageModalities.includes(ds.Modality) || ds.thumbnailSrc === null
    );

    if (!currentDisplaySets.length) {
      return;
    }

    currentDisplaySets.forEach(async dSet => {
      const newImageSrcEntry = {};
      const displaySet = displaySetService.getDisplaySetByUID(dSet.displaySetInstanceUID);
      const imageIds = dataSource.getImageIdsForDisplaySet(dSet);

      const imageId = getImageIdForThumbnail(displaySet, imageIds);

      // TODO: Is it okay that imageIds are not returned here for SR displaySets?
      if (displaySet?.unsupported) {
        return;
      }
      // When the image arrives, render it and store the result in the thumbnailImgSrcMap
      let { thumbnailSrc } = displaySet;
      if (!thumbnailSrc && displaySet.getThumbnailSrc) {
        thumbnailSrc = await displaySet.getThumbnailSrc({ getImageSrc });
      }
      if (!thumbnailSrc && imageId) {
        const thumbnailSrc = await getImageSrc(imageId);
        displaySet.thumbnailSrc = thumbnailSrc;
      }
      newImageSrcEntry[dSet.displaySetInstanceUID] = thumbnailSrc;

      setThumbnailImageSrcMap(prevState => {
        return { ...prevState, ...newImageSrcEntry };
      });
    });
  }, [displaySetService, dataSource, getImageSrc, activeViewportId, hasLoadedViewports]);

  // ~~ displaySets
  useEffect(() => {
    const currentDisplaySets = displaySetService.activeDisplaySets;

    if (!currentDisplaySets.length) {
      return;
    }

    const mappedDisplaySets = mapDisplaySetsWithState(
      currentDisplaySets,
      displaySetsLoadingState,
      thumbnailImageSrcMap,
      viewports
    );

    if (!customMapDisplaySets) {
      sortStudyInstances(mappedDisplaySets);
    }

    setDisplaySets(mappedDisplaySets);
  }, [
    displaySetService.activeDisplaySets,
    displaySetsLoadingState,
    viewports,
    thumbnailImageSrcMap,
    customMapDisplaySets,
  ]);

  // ~~ subscriptions --> displaySets
  useEffect(() => {
    // DISPLAY_SETS_ADDED returns an array of DisplaySets that were added
    const SubscriptionDisplaySetsAdded = displaySetService.subscribe(
      displaySetService.EVENTS.DISPLAY_SETS_ADDED,
      data => {
        if (!hasLoadedViewports) {
          return;
        }
        const { displaySetsAdded, options } = data;
        displaySetsAdded.forEach(async dSet => {
          const displaySetInstanceUID = dSet.displaySetInstanceUID;
          const newImageSrcEntry = {};
          const displaySet = displaySetService.getDisplaySetByUID(displaySetInstanceUID);
          if (displaySet?.unsupported) {
            return;
          }
          if (options?.madeInClient) {
            setJumpToDisplaySet(displaySetInstanceUID);
          }

          const imageIds = dataSource.getImageIdsForDisplaySet(displaySet);
          const imageId = getImageIdForThumbnail(displaySet, imageIds);

          // TODO: Is it okay that imageIds are not returned here for SR displaysets?
          if (!imageId) {
            return;
          }

          // When the image arrives, render it and store the result in the thumbnailImgSrcMap
          let { thumbnailSrc } = displaySet;
          if (!thumbnailSrc && displaySet.getThumbnailSrc) {
            thumbnailSrc = await displaySet.getThumbnailSrc({ getImageSrc });
          }
          if (!thumbnailSrc) {
            thumbnailSrc = await getImageSrc(imageId);
            displaySet.thumbnailSrc = thumbnailSrc;
          }
          newImageSrcEntry[displaySetInstanceUID] = thumbnailSrc;

          setThumbnailImageSrcMap(prevState => {
            return { ...prevState, ...newImageSrcEntry };
          });
        });
      }
    );

    return () => {
      SubscriptionDisplaySetsAdded.unsubscribe();
    };
  }, [displaySetService, dataSource, getImageSrc, hasLoadedViewports]);

  useEffect(() => {
    // TODO: Will this always hold _all_ the displaySets we care about?
    // DISPLAY_SETS_CHANGED returns `DisplaySerService.activeDisplaySets`
    const SubscriptionDisplaySetsChanged = displaySetService.subscribe(
      displaySetService.EVENTS.DISPLAY_SETS_CHANGED,
      changedDisplaySets => {
        const mappedDisplaySets = mapDisplaySetsWithState(
          changedDisplaySets,
          displaySetsLoadingState,
          thumbnailImageSrcMap,
          viewports
        );

        if (!customMapDisplaySets) {
          sortStudyInstances(mappedDisplaySets);
        }

        setDisplaySets(mappedDisplaySets);
      }
    );

    const SubscriptionDisplaySetMetaDataInvalidated = displaySetService.subscribe(
      displaySetService.EVENTS.DISPLAY_SET_SERIES_METADATA_INVALIDATED,
      () => {
        const mappedDisplaySets = mapDisplaySetsWithState(
          displaySetService.getActiveDisplaySets(),
          displaySetsLoadingState,
          thumbnailImageSrcMap,
          viewports
        );

        if (!customMapDisplaySets) {
          sortStudyInstances(mappedDisplaySets);
        }

        setDisplaySets(mappedDisplaySets);
      }
    );

    return () => {
      SubscriptionDisplaySetsChanged.unsubscribe();
      SubscriptionDisplaySetMetaDataInvalidated.unsubscribe();
    };
  }, [
    displaySetsLoadingState,
    thumbnailImageSrcMap,
    viewports,
    displaySetService,
    customMapDisplaySets,
  ]);

  const displaySetsForTabs = useMemo(() => {
    if (!isSelectionMode && !selectedImageIds.length) {
      return displaySets;
    }

    return displaySets.map(mappedDisplaySet => {
      const imageIds = getDisplaySetImageIds(
        mappedDisplaySet.displaySetInstanceUID,
        servicesManager,
        extensionManager
      );

      return {
        ...mappedDisplaySet,
        isDownloadSelected:
          imageIds.length > 0 && imageIds.every(imageId => selectedImageIds.includes(imageId)),
      };
    });
  }, [displaySets, extensionManager, isSelectionMode, selectedImageIds, servicesManager]);

  const tabs = useMemo(
    () => createStudyBrowserTabs(StudyInstanceUIDs, studyDisplayList, displaySetsForTabs),
    [StudyInstanceUIDs, studyDisplayList, displaySetsForTabs]
  );

  // TODO: Should not fire this on "close"
  function _handleStudyClick(StudyInstanceUID) {
    const shouldCollapseStudy = expandedStudyInstanceUIDs.includes(StudyInstanceUID);
    const updatedExpandedStudyInstanceUIDs = shouldCollapseStudy
      ? [...expandedStudyInstanceUIDs.filter(stdyUid => stdyUid !== StudyInstanceUID)]
      : [...expandedStudyInstanceUIDs, StudyInstanceUID];

    setExpandedStudyInstanceUIDs(updatedExpandedStudyInstanceUIDs);

    if (!shouldCollapseStudy) {
      if (isImportedStudyUID(StudyInstanceUID)) {
        handleImportedStudySelect(StudyInstanceUID);
        return;
      }

      const madeInClient = true;
      requestDisplaySetCreationForStudy(displaySetService, StudyInstanceUID, madeInClient);
    }
  }

  useEffect(() => {
    if (jumpToDisplaySet) {
      // Get element by displaySetInstanceUID
      const displaySetInstanceUID = jumpToDisplaySet;
      const element = document.getElementById(`thumbnail-${displaySetInstanceUID}`);

      if (element && typeof element.scrollIntoView === 'function') {
        // TODO: Any way to support IE here?
        element.scrollIntoView({ behavior: 'smooth' });

        setJumpToDisplaySet(null);
      }
    }
  }, [jumpToDisplaySet, expandedStudyInstanceUIDs, activeTabName]);

  useEffect(() => {
    if (!jumpToDisplaySet) {
      return;
    }

    const displaySetInstanceUID = jumpToDisplaySet;
    // Set the activeTabName and expand the study
    const thumbnailLocation = _findTabAndStudyOfDisplaySet(displaySetInstanceUID, tabs);
    if (!thumbnailLocation) {
      return;
    }
    const { tabName, StudyInstanceUID } = thumbnailLocation;
    setActiveTabName(tabName);
    const studyExpanded = expandedStudyInstanceUIDs.includes(StudyInstanceUID);
    if (!studyExpanded) {
      const updatedExpandedStudyInstanceUIDs = [...expandedStudyInstanceUIDs, StudyInstanceUID];
      setExpandedStudyInstanceUIDs(updatedExpandedStudyInstanceUIDs);
    }
  }, [expandedStudyInstanceUIDs, jumpToDisplaySet, tabs]);

  const activeDisplaySetInstanceUIDs = viewports.get(activeViewportId)?.displaySetInstanceUIDs;

  const seriesSummary = useMemo(() => {
    const seriesCount = displaySets.length;
    const imageCount = displaySets.reduce((sum, ds) => {
      const n =
        typeof ds.numImages === 'number'
          ? ds.numImages
          : typeof ds.numImageFrames === 'number'
            ? ds.numImageFrames
            : Array.isArray(ds.instances)
              ? ds.instances.length
              : 1;
      return sum + Math.max(0, n);
    }, 0);
    return { seriesCount, imageCount, studyCount: studyDisplayList.length };
  }, [displaySets, studyDisplayList.length]);

  const studyPreviewItems = useMemo(() => {
    return _sortStudiesByDateDesc(studyDisplayList).map(study => {
      const studyDisplaySets = displaySets.filter(
        ds => ds.StudyInstanceUID === study.studyInstanceUid
      );
      const previewDisplaySet = studyDisplaySets.find(
        ds => ds.imageSrc || thumbnailImageSrcMap[ds.displaySetInstanceUID]
      );
      const imageSrc =
        previewDisplaySet?.imageSrc ||
        (previewDisplaySet
          ? thumbnailImageSrcMap[previewDisplaySet.displaySetInstanceUID]
          : undefined);

      return {
        studyInstanceUid: study.studyInstanceUid,
        dateLabel: _formatStudyDateLabel(study),
        description: study.description,
        imageSrc,
        isCurrentStudy: study.studyInstanceUid === primaryStudyInstanceUID,
      };
    });
  }, [studyDisplayList, displaySets, thumbnailImageSrcMap, primaryStudyInstanceUID]);

  const handleStudyPreviewDoubleClick = useCallback(
    (studyInstanceUid: string) => {
      setShowStudyPreviews(false);
      setExpandedStudyInstanceUIDs(prev =>
        prev.includes(studyInstanceUid) ? prev : [...prev, studyInstanceUid]
      );
      requestDisplaySetCreationForStudy(displaySetService, studyInstanceUid, true);
    },
    [displaySetService, requestDisplaySetCreationForStudy]
  );

  const handleStudyPreviewClick = useCallback(
    (studyInstanceUid: string, event?: React.MouseEvent) => {
      if (patientMode) {
        handleStudyPreviewDoubleClick(studyInstanceUid);
        return;
      }

      const isMultiSelect = event?.ctrlKey || event?.metaKey;

      if (isMultiSelect) {
        setSelectedStudyInstanceUIDs(prev => {
          if (prev.includes(studyInstanceUid)) {
            return prev.filter(uid => uid !== studyInstanceUid);
          }
          return [...prev, studyInstanceUid];
        });
        return;
      }

      setSelectedStudyInstanceUIDs([studyInstanceUid]);

      if (!primaryStudyInstanceUID) {
        return;
      }

      if (studyInstanceUid === primaryStudyInstanceUID) {
        commandsManager.run('viewSingleStudy', { studyInstanceUID: studyInstanceUid });
        return;
      }

      commandsManager.run('compareStudySideBySide', {
        currentStudyInstanceUID: primaryStudyInstanceUID,
        priorStudyInstanceUID: studyInstanceUid,
        enableSync: true,
      });
    },
    [commandsManager, primaryStudyInstanceUID, patientMode, handleStudyPreviewDoubleClick]
  );

  return (
    <div
      className="panel-study-browser flex h-full min-h-0 flex-col overflow-hidden"
      data-cy="panel-study-browser"
    >
      <div className="shrink-0">
        <PanelStudyBrowserHeader
          seriesSummary={seriesSummary}
          viewPresets={viewPresets}
          updateViewPresetValue={updateViewPresetValue}
          actionIcons={actionIcons}
          updateActionIconValue={updateActionIconValue}
          toolbarOnly={studySeriesToolbarOnly}
          showStudyPreviews={showStudyPreviews}
          onToggleStudyPreviews={
            patientMode ? undefined : () => setShowStudyPreviews(prev => !prev)
          }
        />
        <Separator
          orientation="horizontal"
          className="bg-viewer-border"
          thickness="2px"
        />
      </div>

      <ScrollArea className="min-h-0 flex-1" type="always">
        <ImportedStudyPanel onSelectStudy={handleImportedStudySelect} />

        {showStudyPreviews ? (
          <StudyPreviewBrowser
            studies={studyPreviewItems}
            selectedStudyInstanceUIDs={selectedStudyInstanceUIDs}
            compareStudyInstanceUID={compareStudyInstanceUID}
            onStudyClick={handleStudyPreviewClick}
            onStudyDoubleClick={handleStudyPreviewDoubleClick}
            scrollable={false}
          />
        ) : (
          <StudyBrowser
            tabs={tabs}
            servicesManager={servicesManager}
            activeTabName={activeTabName}
            expandedStudyInstanceUIDs={expandedStudyInstanceUIDs}
            onClickStudy={_handleStudyClick}
            onClickTab={clickedTabName => {
              setActiveTabName(clickedTabName);
            }}
            onClickUntrack={onClickUntrack}
            onClickThumbnail={(displaySetInstanceUID, event) => {
              if (isSelectionMode) {
                event?.preventDefault?.();
                event?.stopPropagation?.();

                const displaySet = displaySetService.getDisplaySetByUID(displaySetInstanceUID);
                if (!displaySet || thumbnailNoImageModalities.includes(displaySet.Modality)) {
                  return;
                }

                const imageIds = getDisplaySetImageIds(
                  displaySetInstanceUID,
                  servicesManager,
                  extensionManager
                );

                if (!imageIds.length) {
                  return;
                }

                toggleImageIds(imageIds, displaySet.StudyInstanceUID);
                return;
              }

              const displaySet = displaySetService.getDisplaySetByUID(displaySetInstanceUID);
              if (displaySet && thumbnailNoImageModalities.includes(displaySet.Modality)) {
                onDoubleClickThumbnailHandler(displaySetInstanceUID);
              }
            }}
            onDoubleClickThumbnail={(displaySetInstanceUID, event) => {
              if (isSelectionMode) {
                event?.preventDefault?.();
                event?.stopPropagation?.();
                return;
              }

              onDoubleClickThumbnailHandler(displaySetInstanceUID);
            }}
            activeDisplaySetInstanceUIDs={activeDisplaySetInstanceUIDs}
            showSettings={actionIcons.find(icon => icon.id === 'settings')?.value}
            viewPresets={viewPresets}
            scrollable={false}
            ThumbnailMenuItems={MoreDropdownMenu({
              commandsManager,
              servicesManager,
              menuItemsKey: 'studyBrowser.thumbnailMenuItems',
            })}
            StudyMenuItems={MoreDropdownMenu({
              commandsManager,
              servicesManager,
              menuItemsKey: 'studyBrowser.studyMenuItems',
            })}
          />
        )}
      </ScrollArea>

      <div className="study-browser-download-footer shrink-0">
        <StudyBrowserDownloadButton />
      </div>
    </div>
  );
}

export default PanelStudyBrowser;

/**
 * Maps from the DataSource's format to a naturalized object
 *
 * @param {*} studies
 */
function _mapDataSourceStudies(studies) {
  return studies.map(study => {
    // TODO: Why does the data source return in this format?
    return {
      AccessionNumber: study.accession,
      StudyDate: study.date,
      StudyDescription: study.description,
      NumInstances: study.instances,
      ModalitiesInStudy: study.modalities,
      PatientID: study.mrn,
      PatientName: study.patientName,
      StudyInstanceUID: study.studyInstanceUid,
      StudyTime: study.time,
    };
  });
}

function _mapDisplaySets(displaySets, displaySetLoadingState, thumbnailImageSrcMap, viewports) {
  const thumbnailDisplaySets = [];
  const thumbnailNoImageDisplaySets = [];
  displaySets
    .filter(ds => !ds.excludeFromThumbnailBrowser)
    .forEach(ds => {
      const { thumbnailSrc, displaySetInstanceUID } = ds;
      const componentType = _getComponentType(ds);

      const array =
        componentType === 'thumbnail' ? thumbnailDisplaySets : thumbnailNoImageDisplaySets;

      const loadingProgress = displaySetLoadingState?.[displaySetInstanceUID];

      array.push({
        displaySetInstanceUID,
        description: ds.SeriesDescription || '',
        seriesNumber: ds.SeriesNumber,
        modality: ds.Modality,
        seriesDate: formatDate(ds.SeriesDate),
        numInstances: ds.numImageFrames,
        loadingProgress,
        countIcon: ds.countIcon,
        messages: ds.messages,
        StudyInstanceUID: ds.StudyInstanceUID,
        componentType,
        imageSrc: thumbnailSrc || thumbnailImageSrcMap[displaySetInstanceUID],
        dragData: {
          type: 'displayset',
          displaySetInstanceUID,
          // .. Any other data to pass
        },
        isHydratedForDerivedDisplaySet: ds.isHydrated,
      });
    });

  return [...thumbnailDisplaySets, ...thumbnailNoImageDisplaySets];
}

function _getComponentType(ds) {
  if (
    thumbnailNoImageModalities.includes(ds.Modality) ||
    ds?.unsupported ||
    ds.thumbnailSrc === null
  ) {
    return 'thumbnailNoImage';
  }

  return 'thumbnail';
}

function getImageIdForThumbnail(displaySet, imageIds) {
  let imageId;
  if (displaySet.isDynamicVolume) {
    const timePoints = displaySet.dynamicVolumeInfo.timePoints;
    const middleIndex = Math.floor(timePoints.length / 2);
    const middleTimePointImageIds = timePoints[middleIndex];
    imageId = middleTimePointImageIds[Math.floor(middleTimePointImageIds.length / 2)];
  } else {
    imageId = imageIds[Math.floor(imageIds.length / 2)];
  }
  return imageId;
}

function _findTabAndStudyOfDisplaySet(displaySetInstanceUID, tabs) {
  for (let t = 0; t < tabs.length; t++) {
    const { studies } = tabs[t];

    for (let s = 0; s < studies.length; s++) {
      const { displaySets } = studies[s];

      for (let d = 0; d < displaySets.length; d++) {
        const displaySet = displaySets[d];

        if (displaySet.displaySetInstanceUID === displaySetInstanceUID) {
          return {
            tabName: tabs[t].name,
            StudyInstanceUID: studies[s].studyInstanceUid,
          };
        }
      }
    }
  }
}

function _getStudySortTimestamp(study) {
  const dateMs = _parseDicomDate(study?.rawStudyDate || study?.date);
  if (!dateMs) {
    return 0;
  }

  if (!study?.studyTime) {
    return dateMs;
  }

  const timeRaw = String(study.studyTime).replace(/\D/g, '').padEnd(6, '0').slice(0, 6);
  const hours = Number.parseInt(timeRaw.slice(0, 2), 10) || 0;
  const minutes = Number.parseInt(timeRaw.slice(2, 4), 10) || 0;
  const seconds = Number.parseInt(timeRaw.slice(4, 6), 10) || 0;

  return dateMs + ((hours * 3600 + minutes * 60 + seconds) * 1000 || 0);
}

function _compareStudyDates(studyA, studyB) {
  return _getStudySortTimestamp(studyA) - _getStudySortTimestamp(studyB);
}

function mergeImportedStudiesIntoList(studies) {
  const mergedStudies = [...studies];

  getImportedStudyBrowserEntries().forEach(study => {
    if (!mergedStudies.find(existing => existing.studyInstanceUid === study.studyInstanceUid)) {
      mergedStudies.push(study);
    }
  });

  return mergedStudies;
}

function _sortStudiesByDateDesc(studies) {
  return [...studies].sort((studyA, studyB) => _compareStudyDates(studyB, studyA));
}

function _parseDicomDate(value) {
  if (!value) {
    return 0;
  }
  if (/^\d{8}$/.test(String(value))) {
    const raw = String(value);
    return Date.parse(`${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`);
  }
  return Date.parse(value) || 0;
}

function _formatStudyDateLabel(study) {
  if (!study?.date && !study?.rawStudyDate) {
    return 'Unknown date';
  }

  const formattedDate = study.date || formatDate(study.rawStudyDate);
  if (!study?.studyTime || study.studyTime.length < 4) {
    return formattedDate;
  }

  const timeRaw = String(study.studyTime).padEnd(6, '0');
  const hours = timeRaw.slice(0, 2);
  const minutes = timeRaw.slice(2, 4);
  return `${formattedDate}, ${hours}:${minutes}`;
}
